import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import T from 'i18n-react';
import dynamic from 'next/dynamic';

import { Spacer } from '@motorway/motorway-component-library';

import cypressIds from 'Components/cypress_ids.json';
import { SeenFilter, useSeenFilter } from 'Components/VehicleList/SeenFilter';
import VehicleSort from 'Components/VehicleList/VehicleSort/VehicleSort';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import {
	triggerClearAllFiltersButtonClickedEvent,
	triggerCollectionToggleSelectedEvent,
	triggerShowVehiclesButtonClickedEvent,
} from 'Services/analytics/events';
import { useSeenFilterExperimentation } from 'Services/optimizely/experimentations/useSeenFilterExperimentation';
import {
	useFilters,
	useFiltersConfig,
	useSetFilter,
} from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { applyCypressData } from 'Utilities/helpers';
import { useIsMobilizeMarketplaceVisible } from 'Utilities/hooks/useIsMobilizeMarketplaceVisible';
import { useResetFilters } from 'Utilities/hooks/vehicleList/useResetFilters';
import { VIEWING_FILTER } from 'Utilities/seenVehicles/seenVehicles';

import { joinSortParams } from '../VehicleList.helpers';

import AdditionalFiltersView from './AdditionalFilters/AdditionalFiltersView';
import CollectionToggleView from './CollectionToggle/CollectionToggleView';
import { MakeMultiSelect } from './MakeFilter/MakeMultiSelect';
import ModelMultiSelect from './ModelFilter/ModelMultiSelect';
import SellerTypeFilter from './SellerTypeFilter/SellerTypeFilter';
import VehicleClass from './VehicleClass/VehicleClass';
import Actions from './Actions';
import ButtonsSection from './ButtonsSection';
import { PreviousOwnersFilter } from './PreviousOwnersFilter';
import SlidersSection from './SlidersSection';
import useFilterView from './useFilterView';

import oldStyles from './Filters.module.scss';
import styles from './NewFilters.module.scss';

T.setTexts(require('../../texts.json'));

const Filters = ({ dealerLatLng, dealerPostcode, isLoading, sortParams, togglePanelFilters, vehiclesCount }) => {
	const { isExpandableFilter } = useFilterView();
	const { isAvailableForCollection } = useFilters();
	const setFilter = useSetFilter();
	const filtersConfig = useFiltersConfig();
	const resetFilters = useResetFilters();
	const {
		maxWidth: { breakpointMobile: isMobile },
	} = useBreakpoint();
	const isSeenFilterEnabled = useSeenFilterExperimentation();
	const shouldPromoteSellerTypeFilter = useIsMobilizeMarketplaceVisible();

	const onShowSelectedVehiclesClick = () => {
		triggerShowVehiclesButtonClickedEvent();
		togglePanelFilters();
	};

	const handleCollectionToggle = () => {
		const nextIsAvailableForCollection = !isAvailableForCollection;
		triggerCollectionToggleSelectedEvent(nextIsAvailableForCollection);
		setFilter({ id: filtersConfig.isAvailableForCollection.id, value: nextIsAvailableForCollection });
	};

	const [, setSeenFilter] = useSeenFilter();

	const clearFilters = () => {
		triggerClearAllFiltersButtonClickedEvent();
		resetFilters();
		setSeenFilter(VIEWING_FILTER.ALL);
	};

	return (
		<div className={styles.wrapper} {...applyCypressData(cypressIds.containers.filters)}>
			<div className={styles.sortOrderContainer}>
				<h4 className={cx(styles.title, oldStyles.sidebarHeader)}>{T.translate('vehicleList.sortLabel')}</h4>
				<VehicleSort
					className={styles.vehicleSort}
					dealerLatLng={dealerLatLng}
					dealerPostcode={dealerPostcode}
					selected={joinSortParams(sortParams)}
				/>
				<Spacer showDivider className={cx(styles.divider, 'mw-mt-1 mw-mb-3', { 'mw-mt-2': isMobile })} size="zero" />
			</div>
			<h4 className={cx(styles.title, styles.title, oldStyles.sidebarHeader)}>
				{T.translate('vehicleList.filterLabel')}
			</h4>
			<VehicleClass />
			<Spacer showDivider className={cx(styles.divider, 'mw-mt-1 mw-mb-3', { 'mw-mt-2': isMobile })} size="zero" />
			{shouldPromoteSellerTypeFilter && (
				<>
					<SellerTypeFilter />
					<Spacer showDivider className={cx(styles.divider, 'mw-mt-1 mw-mb-3', { 'mw-mt-2': isMobile })} size="zero" />
				</>
			)}
			<MakeMultiSelect />
			<ModelMultiSelect />
			<Spacer showDivider className={cx(styles.divider)} size="zero" />
			<SlidersSection dealerLatLng={dealerLatLng} dealerPostcode={dealerPostcode} />
			<Spacer showDivider className={cx(styles.divider)} size="zero" />
			<PreviousOwnersFilter />
			<Spacer showDivider className={cx(styles.divider, { 'mw-mt-3': !isExpandableFilter })} size="zero" />
			{isSeenFilterEnabled ? (
				<>
					<SeenFilter />
					<Spacer showDivider className={cx(styles.divider)} size="zero" />
				</>
			) : null}
			<ButtonsSection showSellerTypeFilter={!shouldPromoteSellerTypeFilter} />
			<Spacer showDivider className={cx(styles.divider)} size="zero" />
			<CollectionToggleView
				handleCollectionToggle={handleCollectionToggle}
				id="collectionToggle"
				isAvailableForCollection={Boolean(isAvailableForCollection)}
			/>
			<Spacer showDivider className={cx(styles.divider, { 'mw-mt-3': !isExpandableFilter })} size="zero" />
			<AdditionalFiltersView />
			{isExpandableFilter ? <Spacer showDivider className={cx(styles.divider)} size="zero" /> : null}
			{!isExpandableFilter && (
				<Actions
					clearFilters={clearFilters}
					isLoading={isLoading}
					onShowSelectedVehiclesClick={onShowSelectedVehiclesClick}
					vehiclesCount={vehiclesCount}
				/>
			)}
		</div>
	);
};

Filters.defaultProps = {
	dealerPostcode: '',
	vehiclesCount: null,
};

Filters.propTypes = {
	dealerLatLng: PropTypes.arrayOf(PropTypes.number).isRequired,
	dealerPostcode: PropTypes.string,
	isLoading: PropTypes.bool.isRequired,
	togglePanelFilters: PropTypes.func.isRequired,
	vehiclesCount: PropTypes.number,
};

// Dynamically load Filters and block SSR to avoid flash of empty expanded filters
export default dynamic(() => Promise.resolve(Filters), {
	ssr: false,
});
